import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-02-18 19:10:56 +0100 (Wed, 18 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160820 $
//  Purpose   : plots for stats
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Dropdown, Button, Popup, Card } from 'semantic-ui-react';
// ---Framework imports
import { Sel } from 'uix/registries';
import log from 'uix/logging';
import { dict, mapItems, sum, sorted } from 'ldash';
import { filterItems, items, isString } from 'ldash';
import { AutoSizer, HeaderSegment, XMenu } from 'uix/components';
import { useChunk, usePersistentShallow, useSelector, useApi, useMemo, useState, useEffect } from 'uix/hooks';
import { JSONTree } from 'uix/chunks';
import { VIcon } from 'uix/graphics';
import { formatSI, hashObj } from 'uix/utils';
import { subDays, formatDate } from 'uix/utils/date';
import { translateState } from 'uix/states';
import { lightColors, statusColors } from 'uix/utils/colors';
import { isoToday, granularity2interval, isoDate } from 'uix/utils/date';
// ---Globals
const DELTA = String.fromCodePoint(0x0394);
const REMOTE_ENGINE_PLOTS = dict([
    [
        'ocr_finereader_0',
        'Finereader (Antiqua)'
    ],
    [
        'ocr_finereader_1',
        'Finereader (Fraktur)'
    ],
    [
        'ocr_google_0',
        'Google (OCR)'
    ],
    [
        'ocr_google_docai_0',
        'Google-DocAi (OCR)'
    ],
    [
        'ocr_tesseract_0',
        'Tesseract (OCR)'
    ],
    [
        'ocr_microsoft_0',
        'Microsoft (OCR)'
    ],
    [
        'ner_google_0',
        'Google (NER)'
    ]
].map(([field, title])=>[
        field,
        {
            title,
            apiparams: 'remoteoperation,opts',
            field
        }
    ]));
const GROWTH_PLOTS = {
    countMetadata: {
        title: 'Import Katalogisate',
        apiparams: 'datatype1,inserttime'
    },
    countStructure: {
        title: 'Erstellung Strukturen',
        apiparams: 'datatype0,inserttime'
    },
    countPages: {
        title: 'Import Digitalisate',
        apiparams: 'datatype3,inserttime'
    },
    countPDF: {
        title: 'Erstellung PDFs',
        apiparams: 'resource1,inserttime'
    },
    countFulltext: {
        title: 'Erstellung Volltexte',
        apiparams: 'resource2,inserttime'
    },
    // all in state=1
    released: {
        title: 'Freigegeben',
        apiparams: 'sysaggr,ts',
        field: 'count_rel_1'
    },
    unreleased: {
        title: 'Nicht Freigegeben',
        apiparams: 'sysaggr,ts',
        field: 'count_rel_0'
    },
    // all in some qs-states
    qs_in_qa: {
        title: 'QS: aktiv',
        apiparams: 'sysaggr,ts',
        field: 'count_qa_inqa'
    },
    qs_error: {
        title: 'QS: Fehler',
        apiparams: 'sysaggr,ts',
        field: 'count_qa_error'
    },
    ...REMOTE_ENGINE_PLOTS,
    metadata_complete: {
        title: 'Metadata: komplett',
        apiparams: 'sysaggr,ts',
        field: 'count_metadata_complete'
    },
    stores: {
        title: 'Stores',
        apiparams: 'sysaggr,ts',
        stackmode: 'fields',
        fields: 'sum_store_filesize_',
        stackinfo: (field_name, i18n, i)=>({
                label: field_name.split('_').slice(-1)[0],
                color: lightColors[i % lightColors.length]
            })
    },
    //~ release: {title: 'In QS', apiparams: 'sysaggr,ts', fields: 'count_qa_inqa'},
    zipcapsule: {
        title: 'Kapselstatus bei Titeln',
        apiparams: 'sysaggr,ts',
        fields: 'count_zipcapsule_',
        inactive: [
            'count_zipcapsule_0'
        ],
        stackmode: 'fields',
        stackinfo: (field_name, i18n, i)=>{
            let state_name = field_name.split('_').slice(-1)[0];
            let color_map = {
                '-1': statusColors.inactive,
                0: statusColors.success2,
                1: statusColors.neutral,
                6: statusColors.error,
                7: statusColors.neutral2,
                8: statusColors.error2
            };
            return {
                label: translateState(i18n, 'zipcapsule', state_name),
                color: color_map[state_name] || 'var(--vl_accent_color)'
            };
        }
    }
};
const getPlotCfg = (name)=>{
    // apply config defaults here
    let { field = 'count', apiparams, subdomains = true, stackmode = 'subdomains', ...cfg } = GROWTH_PLOTS[name];
    let [tstype, datefield] = apiparams.split(',');
    return {
        ...cfg,
        tstype,
        datefield,
        aggrfield: field,
        subdomains,
        stackmode
    };
};
const transformToDomainGrowthPlot = (dname, allDomains, stackeddata, aggrfield, cutoff)=>{
    /* transform raw timestats output into plottable format
   */ let { tsdata, tstype, stackmode } = stackeddata;
    // only one timestats type has absolute values (for now)
    let field_type_abs = tstype == 'sysaggr';
    let parseNum = (e)=>{
        if (e === undefined) {
            return 0;
        } else if (isString(e)) {
            return e.includes('.') ? parseFloat(e) : parseInt(e);
        } else {
            return e;
        }
    };
    let buildStack = (stack_tsdata)=>{
        let last_abs = null // different field_type_abs init, see below
        ;
        let buildAbsoluteAndDelta = (fields)=>{
            // some timestats type collect absolute and some collect delta values
            // normalize here into both forms
            let delta = null;
            let absolute = null;
            let val = fields[aggrfield];
            if (field_type_abs) {
                absolute = parseNum(val);
                delta = absolute - (last_abs === null ? absolute : last_abs);
            } else {
                delta = parseNum(val);
                absolute = (last_abs === null ? 0 : last_abs) + delta;
            }
            last_abs = absolute;
            return {
                absolute,
                delta
            };
        };
        return stack_tsdata.map((ivdata)=>{
            let { date, datetime, ...fields } = ivdata;
            datetime = datetime.split(' ')[0];
            let { absolute, delta } = buildAbsoluteAndDelta(fields);
            let x = new Date(datetime).getTime() / 1000;
            return {
                x,
                date: x,
                datetime,
                absolute,
                delta
            };
        });
    };
    // normalize all domains
    tsdata = dict(mapItems(tsdata, (sn, data)=>[
            sn,
            buildStack(data)
        ]));
    let current_domain = tsdata[dname] || [];
    let dates = current_domain.map((e)=>e.x);
    let [startDate, endDate] = [
        current_domain[0]?.datetime,
        current_domain.slice(-1)[0]?.datetime
    ];
    // remove current domain and empty stacks
    // keep only direct children
    let stack_filter = (dn, data)=>dn !== dname;
    if (stackmode == 'subdomains') {
        stack_filter = (dn, data)=>dn !== dname && allDomains[dn].parent == dname && data.length > 0;
    }
    let stack_data = mapItems(tsdata, (dn, data)=>[
            dn,
            dict(data.map((e)=>[
                    e.date,
                    e
                ]))
        ], stack_filter);
    // remember last absolute values for stacks (for fill up)
    let last_abs = {};
    // consolidate stacks into {name: {absolute, delta}} form
    let current_with_stacks = current_domain.map((current_data)=>{
        // stack domains
        let stacks = dict(stack_data.map(([dn, data])=>{
            // keep only absolute and delta value from stacked data (throw away date info)
            let d = data[current_data.date];
            if (d) {
                d = {
                    absolute: d.absolute,
                    delta: d.delta
                };
                last_abs[dn] = d.absolute;
            } else {
                // fill up
                d = {
                    absolute: last_abs[dn] || 0,
                    delta: 0
                };
            }
            return [
                dn,
                d
            ];
        }));
        return {
            ...current_data,
            stacks
        };
    });
    // descending sorted stack names
    let keep_stacks = sorted(items(last_abs), ([sn, absolute])=>absolute, true).map((e)=>e[0]);
    if (cutoff) {
        keep_stacks = keep_stacks.slice(0, cutoff);
        // combine all cutoffs into "other" stack
        current_with_stacks = current_with_stacks.map((e)=>{
            let stacks_with_cutoff = dict(filterItems(e.stacks, (sn)=>keep_stacks.includes(sn)));
            let stacks_other = filterItems(e.stacks, (sn)=>!keep_stacks.includes(sn)).map((e)=>e[1]);
            let other = {
                absolute: sum(stacks_other.map((e)=>e.absolute)),
                delta: sum(stacks_other.map((e)=>e.delta))
            };
            if (other.absolute > 0) {
                stacks_with_cutoff = {
                    ...stacks_with_cutoff,
                    other
                };
                if (!keep_stacks.includes('other')) {
                    keep_stacks = [
                        ...keep_stacks,
                        'other'
                    ];
                }
            }
            return {
                ...e,
                stacks: stacks_with_cutoff
            };
        });
    }
    return {
        xs: dates,
        startDate,
        endDate,
        data: current_with_stacks,
        stack_names: keep_stacks
    };
};
const restack = (dname, plotcfg, apidata)=>{
    if (!apidata) return apidata;
    if (plotcfg.stackmode == 'fields') {
        let { tsdata } = apidata;
        let main = tsdata[dname];
        if (!main) {
            log.warn('got no tsdata for %o', dname);
            return null;
        }
        let target_field = plotcfg.aggrfield;
        let collected_fields = new Set();
        let collectPrefix = (e)=>{
            let res = sum(mapItems(e, (k, v)=>parseFloat(v), (k)=>{
                let is_field = k.startsWith(plotcfg.fields);
                if (is_field) collected_fields.add(k);
                return is_field;
            }));
            return res || 0;
        };
        //~ log.x(collected_fields)
        let current = main.map((e)=>({
                datetime: e.datetime,
                [target_field]: collectPrefix(e)
            }));
        let stacks = dict([
            ...collected_fields
        ].map((f_name)=>[
                f_name,
                main.map((e)=>({
                        datetime: e.datetime,
                        [target_field]: parseFloat(e[f_name]) || 0
                    }))
            ]));
        apidata = {
            ...apidata,
            tsdata: {
                [dname]: current,
                ...stacks
            }
        };
    }
    return {
        ...apidata,
        stackmode: plotcfg.stackmode
    };
};
const EMPTY_DATA = {
    data: [],
    xs: [],
    stack_names: [],
    apiStartDate: null
};
const usePlotData = ({ plotcfg, startDate, endDate, granularity, cutoff })=>{
    let { aggrfield, tstype, datefield, subdomains, stackmode } = plotcfg;
    let i18n = useSelector(Sel.i18n);
    let { data: allDomains } = useSelector(Sel.domains);
    let { dname } = useSelector(Sel.request);
    let params = {
        type: tstype,
        datefield,
        interval: granularity2interval[granularity],
        subdomains,
        from: startDate,
        until: endDate
    };
    let { data: apidata, error, loading } = useApi('stats/getTimeStatsSlice', params, {
        cached: true,
        // plot renders a nice transition if we dont drop to empty data during load
        autoreset: false
    });
    let minDate = apidata?.minDate?.split('T')[0] || null;
    let stackeddata = useMemo(()=>restack(dname, plotcfg, apidata), [
        dname,
        hashObj(plotcfg),
        apidata
    ]);
    // autoreset = false gives us old data, so use older params to
    //~ let usedAggrField = useMemo(() => aggrfield, [loading])
    let trafoParams = [
        dname,
        allDomains,
        stackeddata,
        aggrfield,
        cutoff
    ];
    let { data, xs, stack_names, startDate: apiStartDate } = useMemo(()=>stackeddata ? transformToDomainGrowthPlot(...trafoParams) : EMPTY_DATA, trafoParams);
    let filtered_stack_names = stack_names.filter((e)=>e !== 'other');
    let stackinfo = {};
    if (plotcfg.stackinfo) {
        stackinfo = dict(filtered_stack_names.map((sn, i)=>[
                sn,
                plotcfg.stackinfo(sn, i18n, i)
            ]));
    } else {
        let color = (sn, i)=>allDomains[sn]?.color || lightColors[i % lightColors.length];
        let label = (sn)=>allDomains[sn]?.description || sn;
        stackinfo = dict(filtered_stack_names.map((sn, i)=>[
                sn,
                {
                    label: label(sn),
                    color: color(sn, i)
                }
            ]));
    }
    if (stack_names.includes('other')) {
        stackinfo = {
            ...stackinfo,
            other: {
                label: 'Andere',
                color: '#CCCCCC'
            }
        };
    }
    return {
        data,
        xs,
        stackinfo,
        minDate,
        apiStartDate
    };
};
const ANIMATION_DURATION = 1000;
export const formatDateWithGranularity = (dt, granularity)=>{
    let date_fmt = {
        Q: 'qqq.yy',
        M: 'LLL. yy',
        W: 'wo yyyy',
        D: 'PP'
    }[granularity] || 'ISO_DATE';
    return formatDate(dt, date_fmt);
};
const configurePlot = ({ i18n, R, width, stackinfo, granularity, xs, set_xs_select = null, inactive, deltaInLog = false, absoluteInLog = false, totalLine = false, totalBar = false, stackLines = false, stackAreas = false, stackBars = false, showBrush = false })=>{
    // up to
    let [lines, bars, areas, xaxis] = [
        [],
        [],
        [],
        null
    ];
    // two axis allowed
    let yaxis = [];
    let inactive_filter = (name)=>!inactive[name];
    if (totalLine) {
        lines.push(/*#__PURE__*/ _jsx(R.Line, {
            name: "Total",
            yAxisId: "absoluteAxis",
            type: "monotone",
            dataKey: "absolute",
            stroke: 'var(--vl_body_color)',
            animationDuration: ANIMATION_DURATION
        }, "total"));
    }
    if (stackLines) {
        lines.push(...mapItems(stackinfo, (name, info)=>/*#__PURE__*/ _jsx(R.Line, {
                name: info.label,
                yAxisId: "absoluteAxis",
                type: "monotone",
                dot: false,
                dataKey: `stacks.${name}.absolute`,
                stroke: info.color,
                animationDuration: ANIMATION_DURATION
            }, `l_${name}`), inactive_filter));
    }
    if (totalBar) {
        bars.push(/*#__PURE__*/ _jsx(R.Bar, {
            name: "Delta",
            yAxisId: "deltaAxis",
            dataKey: `delta`,
            fill: '#333333C4',
            animationDuration: ANIMATION_DURATION
        }, `b_total`));
    }
    if (stackBars) {
        bars.push(...mapItems(stackinfo, (name, info)=>/*#__PURE__*/ _jsx(R.Bar, {
                name: info.label,
                yAxisId: "deltaAxis",
                dataKey: `stacks.${name}.delta`,
                stackId: "a",
                fill: info.color,
                animationDuration: ANIMATION_DURATION
            }, `b_${name}`), inactive_filter));
    }
    if (stackAreas) {
        areas.push(...mapItems(stackinfo, (name, info)=>/*#__PURE__*/ _jsx(R.Area, {
                name: info.label,
                yAxisId: "absoluteAxis",
                dataKey: `stacks.${name}.absolute`,
                stackId: "a",
                stroke: info.color,
                fill: info.color,
                animationDuration: ANIMATION_DURATION
            }, `a_${name}`), inactive_filter));
    }
    let formatDateInterval = (ts)=>{
        if (ts === Infinity || ts === -Infinity || ts === undefined) return null;
        return formatDateWithGranularity(new Date(ts * 1000), granularity);
    };
    let labelStyle = {
        fontSize: '0.9rem',
        fill: '#7a7a7a',
        fontFamily: 'Nunito'
    };
    let axisStyle = {
        fontSize: '0.9rem',
        tick: {
            fill: '#7a7a7a'
        }
    };
    //~ const angledTicks = {height: 30, angle: -30, dy: 8, dx: -15}
    xaxis = /*#__PURE__*/ _jsx(R.XAxis, {
        type: "number",
        domain: [
            'left',
            'right'
        ],
        tickFormatter: formatDateInterval,
        ticks: xs,
        dataKey: "x",
        padding: "gap",
        ...axisStyle
    });
    let absOpts = {};
    if (absoluteInLog) {
        absOpts = {
            ...absOpts,
            scale: 'log',
            domain: [
                'auto',
                'auto'
            ]
        };
    }
    let deltaOpts = {};
    if (deltaInLog) {
        deltaOpts = {
            ...deltaOpts,
            scale: 'log',
            domain: [
                'auto',
                'auto'
            ]
        };
    }
    let formatUnits = (n)=>formatSI(n, 2);
    if (stackBars || totalBar) {
        let label = `${DELTA} ${i18n.get(granularity2interval[granularity])}`;
        yaxis.push(/*#__PURE__*/ _jsx(R.YAxis, {
            yAxisId: "deltaAxis",
            tickFormatter: formatUnits,
            ...axisStyle,
            label: {
                value: label,
                angle: -90,
                position: 'insideLeft',
                offset: 0,
                ...labelStyle
            },
            ...deltaOpts
        }, "dax"));
    }
    if (totalLine || stackAreas || stackLines) {
        yaxis.push(/*#__PURE__*/ _jsx(R.YAxis, {
            yAxisId: "absoluteAxis",
            tickFormatter: formatUnits,
            orientation: "right",
            ...axisStyle,
            label: {
                value: 'Summe',
                angle: 90,
                position: 'insideRight',
                offset: 0,
                ...labelStyle
            },
            ...absOpts
        }, "aax"));
    }
    let tooltip = /*#__PURE__*/ _jsx(R.Tooltip, {
        labelFormatter: formatDateInterval,
        formatter: formatUnits
    });
    let brush = /*#__PURE__*/ _jsx(R.Brush, {
        dataKey: "x",
        tickFormatter: formatDateInterval,
        height: 30,
        stroke: "#8884d8",
        onChange: (e)=>set_xs_select(e)
    });
    return {
        areas,
        bars,
        lines,
        xaxis,
        yaxis,
        tooltip,
        brush
    };
};
let INIT_OPTS = {
    absoluteInLog: false,
    deltaInLog: false,
    totalLine: true,
    totalBar: false,
    stackBars: true,
    stackLines: false,
    stackAreas: false,
    showTooltip: false,
    showBrush: false,
    granularity: 'Q',
    cutoff: 6
};
const decodeMenuOptions = (showMenu)=>{
    if (showMenu === true) {
        return {
            show: true
        };
    } else if (showMenu === 'basic') {
        return {
            show: true
        };
    }
    return {
        show: false
    };
};
const useMouseZoom = ({ R, setStartDate, setEndDate, startDate, endDate, enabled })=>{
    let [startZoomRange, setStartZoomRange] = useState(null);
    let [endZoomRange, setEndZoomRange] = useState(null);
    let [zoomStack, setZoomStack] = useState([]);
    let initZoom = (rangeStart, ctrlKey)=>{
        if (!rangeStart) {
            ctrlKey = true;
        }
        if (ctrlKey) {
            if (zoomStack.length > 0) {
                // go back to old start/end selection
                let [sd, ed] = zoomStack.at(-1);
                setStartDate(sd);
                setEndDate(ed);
                setZoomStack(zoomStack.slice(0, -1));
            }
        } else {
            setStartZoomRange(rangeStart);
        }
    };
    let moveZoom = (rangeEnd)=>startZoomRange && setEndZoomRange(rangeEnd);
    let checkEndZoom = (rangeEnd)=>{
        if (startZoomRange && rangeEnd !== startZoomRange) {
            if (startDate && endDate) {
                // store old start/end selection to stack
                setZoomStack([
                    ...zoomStack,
                    [
                        startDate,
                        endDate
                    ]
                ]);
            }
            //~ log.x(isoDate(new Date(startZoomRange * 1000)), isoDate(new Date(endZoomRange * 1000)))
            setStartDate(isoDate(new Date(startZoomRange * 1000)));
            setEndDate(isoDate(new Date(endZoomRange * 1000)));
        }
        setStartZoomRange(null);
        setEndZoomRange(null);
    };
    let zoomHighlight = endZoomRange && startZoomRange !== endZoomRange && R ? /*#__PURE__*/ _jsx(R.ReferenceArea, {
        yAxisId: "deltaAxis",
        x1: startZoomRange,
        x2: endZoomRange,
        strokeOpacity: 0.3
    }) : null;
    if (!enabled) {
        return {};
    }
    // XXX: disable mouse-zoom if brush active?
    return {
        onMouseDown: (e, evt)=>initZoom(e?.activeLabel, evt.ctrlKey),
        onMouseMove: (e)=>e?.activeLabel && moveZoom(e.activeLabel),
        onMouseUp: (e)=>e?.activeLabel && checkEndZoom(e.activeLabel),
        zoomHighlight
    };
};
export const Plot = ({ name, debug = false, flex = false, width, height, showMenu = false, showLegend = false, menuPortal })=>{
    if (!GROWTH_PLOTS[name]) return `${name} not found`;
    let i18n = useSelector(Sel.i18n);
    let plotcfg = getPlotCfg(name);
    let [opts, setOpts] = usePersistentShallow('plotOpts', INIT_OPTS, 5);
    let setOption = (name, value)=>setOpts({
            ...opts,
            [name]: value
        });
    //~ let [granularity, setGranularity] = useState('M')
    let [endDate, setEndDate] = useState(isoToday());
    let [startDate, setStartDate] = useState(null);
    if (startDate === null) {
        if (opts.granularity === 'D') {
            startDate = isoDate(subDays(new Date(), 30));
        } else if (opts.granularity === 'W') {
            startDate = isoDate(subDays(new Date(), 365));
        }
    }
    // derive default inactiveness from plotcfg bt allow maual override
    let [inactive, setInactive] = useState({});
    inactive = useMemo(()=>plotcfg.inactive ? {
            ...dict(plotcfg.inactive.map((n)=>[
                    n,
                    true
                ])),
            ...inactive
        } : inactive, [
        inactive,
        plotcfg.inactive
    ]);
    let { data, xs, stackinfo, minDate, apiStartDate } = usePlotData({
        plotcfg,
        granularity: opts.granularity,
        endDate,
        startDate,
        cutoff: opts.cutoff
    });
    let [xs_select, set_xs_select] = useState(null);
    useEffect(()=>{
        set_xs_select(null);
    }, [
        xs
    ]);
    let R = useChunk('Recharts');
    let pickerStartDate = startDate || apiStartDate // derive first picker values from api response
    ;
    let { onMouseDown, onMouseMove, onMouseUp, zoomHighlight } = useMouseZoom({
        R,
        setStartDate,
        setEndDate,
        startDate: pickerStartDate,
        endDate,
        // for some obscure reasons ReferenceArea crashes if no bars are drawn
        enabled: opts.totalBar || opts.stackBars
    });
    if (!R) return null;
    if (xs_select) {
        xs = xs.slice(xs_select.startIndex || 0, xs_select.endIndex || null);
    }
    let menuOpts = decodeMenuOptions(showMenu);
    let toggle = (name, label, icon, title)=>/*#__PURE__*/ _jsx(XMenu.Toggle, {
            icon: icon,
            content: label,
            set: (value)=>setOption(name, value),
            value: opts[name],
            title: title
        });
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        padded: true,
        children: [
            /*#__PURE__*/ _jsxs(XMenu.SubMenu, {
                content: "Graphentypen",
                icon: "cog",
                children: [
                    toggle('totalLine', 'Linien (Summe)', 'chart line'),
                    toggle('totalBar', 'Balken (Summe)', 'chart bar'),
                    toggle('stackBars', 'Balken', 'chart bar'),
                    toggle('stackLines', 'Linien', 'chart line'),
                    toggle('stackAreas', 'Flächen (Summe)', 'chart area')
                ]
            }),
            menuOpts.cutoff && /*#__PURE__*/ _jsx(XMenu.Slider, {
                width: "30",
                value: opts.cutoff,
                max: 30,
                onChange: (value)=>parseInt(value) && setOption('cutoff', parseInt(value))
            }),
            /*#__PURE__*/ _jsx(XMenu.DatePicker, {
                quarts: true,
                weeks: true,
                months: true,
                days: true,
                startDate: pickerStartDate,
                endDate: endDate,
                minDate: pickerStartDate < minDate ? pickerStartDate : minDate,
                granularity: opts.granularity,
                onChange: ({ startDate, endDate, granularity })=>{
                    setStartDate(startDate);
                    setEndDate(endDate);
                    setOption('granularity', granularity);
                }
            }),
            showMenu === true && toggle('showTooltip', '', 'crosshairs', 'Tooltip'),
            showMenu === true && toggle('showBrush', '', 'arrows alternate horizontal')
        ]
    });
    let legendPayload = [];
    if ([
        'stackBars',
        'stackLines',
        'stackAreas'
    ].some((o)=>opts[o])) {
        legendPayload.push(...mapItems(stackinfo, (name, info)=>({
                value: info.label,
                type: 'circle',
                id: name,
                color: inactive[name] ? '#EEE' : info.color
            })));
    }
    if (opts.totalLine || opts.totalBar) {
        legendPayload.push({
            value: 'Total',
            type: 'line',
            id: 'total',
            color: '#333'
        });
    }
    let renderLegend = showLegend && legendPayload.length > 0;
    let chart = ({ width, height })=>{
        if (width == 0 || height == 0) return null;
        let { xaxis, yaxis, lines, bars, areas, tooltip, brush } = configurePlot({
            i18n,
            R,
            width,
            stackinfo,
            xs,
            set_xs_select,
            inactive,
            granularity: opts.granularity,
            ...opts
        });
        return /*#__PURE__*/ _jsxs(R.ComposedChart, {
            width: width,
            height: height,
            data: data,
            style: {
                userSelect: 'none'
            },
            margin: {
                top: 0,
                right: 5,
                left: 5,
                bottom: 0
            },
            stackOffset: "sign",
            onMouseDown: onMouseDown,
            onMouseMove: onMouseMove,
            onMouseUp: onMouseUp,
            children: [
                /*#__PURE__*/ _jsx(R.CartesianGrid, {
                    strokeDasharray: "3 3"
                }),
                opts.showBrush && brush,
                opts.showTooltip && tooltip,
                renderLegend && /*#__PURE__*/ _jsx(R.Legend, {
                    payload: legendPayload,
                    onClick: (e)=>setInactive({
                            ...inactive,
                            [e.id]: !inactive[e.id]
                        })
                }),
                xaxis,
                yaxis,
                bars,
                areas,
                lines,
                zoomHighlight
            ]
        });
    };
    let chartContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: {
                    flex: 1
                },
                children: /*#__PURE__*/ _jsx(AutoSizer, {
                    children: chart
                })
            }),
            debug && /*#__PURE__*/ _jsx(JSONTree, {
                hideRoot: true,
                data: {
                    stackinfo,
                    xs,
                    data
                }
            })
        ]
    });
    let m = menuOpts.show ? menu : null;
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: plotcfg.title,
        flex: true,
        height: height,
        basic: true,
        menu: m,
        children: chartContent
    });
};
Plot.PLOTS = GROWTH_PLOTS;
